import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  MapPin,
  Building,
  Route,
  BookOpen,
  ArrowRight,
  TrendingUp } from
'lucide-react';
import { PLACES_CATALOG } from '../data/places-catalog';
import { NEIGHBORHOODS } from '../data/neighborhoods';
import { ITINERARIES } from '../data/itineraries';
import { EDITORIAL_POSTS } from '../data/editorial';
import { useLanguage } from '../contexts/LanguageContext';
import { searchPlaces } from '../lib/api';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
type ResultType = 'place' | 'neighborhood' | 'itinerary' | 'editorial';
interface ResultGroup {
  type: ResultType;
  id: string;
  name: {
    en: string;
    ar: string;
  };
  subtitle: {
    en: string;
    ar: string;
  };
  image?: string;
  /** Searchable extras (tags, category) — improves matching, not displayed */
  keywords?: string;
}

type PlaceSearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; rows: ResultGroup[] }
  | { kind: 'fallback' };
const ICONS = {
  place: MapPin,
  neighborhood: Building,
  itinerary: Route,
  editorial: BookOpen
};
const ROUTES = {
  place: (id: string) => `/places/${id}`,
  neighborhood: (slug: string) => `/neighborhoods/${slug}`,
  itinerary: (id: string) => `/itinerary/${id}`,
  editorial: (slug: string) => `/editorial/${slug}`
};
const MAX_PER_GROUP = 4;
/** Highlight matched substring (case-insensitive) inside a string. */
function HighlightedText({ text, query }: {text: string;query: string;}) {
  if (!query.trim()) return <>{text}</>;
  const q = query.trim();
  const lower = text.toLowerCase();
  const lowerQ = q.toLowerCase();
  const idx = lower.indexOf(lowerQ);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-terracotta/15 text-terracotta rounded px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>);

}
export function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t, ui, isRTL, lang } = useLanguage();
  const [placeSearch, setPlaceSearch] = useState<PlaceSearchState>({ kind: 'idle' });

  const bundledPlaceResults = useMemo<ResultGroup[]>(() => {
    return PLACES_CATALOG.map((p) => {
      const tags = (p as any).tags?.join(' ') || '';
      return {
        type: 'place',
        id: p.id,
        name: p.name,
        subtitle: {
          en: `${p.categoryLabel.en} • ${p.neighborhood.en}`,
          ar: `${p.categoryLabel.ar} • ${p.neighborhood.ar}`,
        },
        image: p.coverImage,
        keywords:
          `${p.categoryLabel.en} ${p.categoryLabel.ar} ${p.neighborhood.en} ${p.neighborhood.ar} ${tags}`.toLowerCase(),
      };
    });
  }, []);

  const nonPlaceResults = useMemo<ResultGroup[]>(() => {
    const results: ResultGroup[] = [];
    NEIGHBORHOODS.forEach((n: any) => {
      results.push({
        type: 'neighborhood',
        id: n.slug,
        name: n.name,
        subtitle: n.tagline || {
          en: 'Neighborhood',
          ar: 'حي',
        },
        image: n.heroImage || n.coverImage,
      });
    });
    ITINERARIES.forEach((it: any) => {
      results.push({
        type: 'itinerary',
        id: it.id,
        name: it.title || it.name,
        subtitle:
          it.theme ||
          it.duration || {
            en: 'Itinerary',
            ar: 'مسار',
          },
        image: it.coverImage || it.heroImage,
      });
    });
    EDITORIAL_POSTS.forEach((a: any) => {
      const sub =
        a.theme ||
        a.excerpt || {
          en: 'Article',
          ar: 'مقال',
        };
      results.push({
        type: 'editorial',
        id: a.slug,
        name: a.title || a.headline,
        subtitle:
          typeof sub === 'object'
            ? sub
            : {
                en: String(sub),
                ar: String(sub),
              },
        image: a.coverImage || a.heroImage,
      });
    });
    return results;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const q = query.trim();
    if (!q) {
      setPlaceSearch({ kind: 'idle' });
      return;
    }
    setPlaceSearch({ kind: 'loading' });
    const timer = window.setTimeout(() => {
      searchPlaces(q)
        .then((rows) => {
          setPlaceSearch({
            kind: 'ok',
            rows: rows.map((r) => ({
              type: 'place' as const,
              id: r.id,
              name: r.name,
              subtitle: {
                en: r.categoryLabel.en,
                ar: r.categoryLabel.ar,
              },
              image: r.coverImage,
              keywords: `${r.categoryLabel.en} ${r.categoryLabel.ar}`.toLowerCase(),
            })),
          });
        })
        .catch(() => {
          console.warn('[api] places search failed');
          if (import.meta.env.DEV) {
            setPlaceSearch({ kind: 'fallback' });
          } else {
            setPlaceSearch({ kind: 'ok', rows: [] });
          }
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setPlaceSearch({ kind: 'idle' });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const fuzzyBundledPlaces = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qRaw = query.trim();
    if (!q) return [] as ResultGroup[];
    return bundledPlaceResults.filter((r) => {
      const en = r.name?.en?.toLowerCase() || '';
      const ar = r.name?.ar || '';
      const subEn = r.subtitle?.en?.toLowerCase() || '';
      const subAr = r.subtitle?.ar || '';
      const kw = r.keywords || '';
      return (
        en.includes(q) ||
        ar.includes(qRaw) ||
        subEn.includes(q) ||
        subAr.includes(qRaw) ||
        kw.includes(q)
      );
    });
  }, [bundledPlaceResults, query]);

  const filteredNonPlaces = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qRaw = query.trim();
    if (!q) return [] as ResultGroup[];
    return nonPlaceResults.filter((r) => {
      const en = r.name?.en?.toLowerCase() || '';
      const ar = r.name?.ar || '';
      const subEn = r.subtitle?.en?.toLowerCase() || '';
      const subAr = r.subtitle?.ar || '';
      const kw = r.keywords || '';
      return (
        en.includes(q) ||
        ar.includes(qRaw) ||
        subEn.includes(q) ||
        subAr.includes(qRaw) ||
        kw.includes(q)
      );
    });
  }, [nonPlaceResults, query]);

  const resolvedPlaceRows = useMemo((): ResultGroup[] => {
    if (!query.trim()) return [];
    if (placeSearch.kind === 'loading') return [];
    if (placeSearch.kind === 'ok') return placeSearch.rows;
    if (placeSearch.kind === 'fallback') return fuzzyBundledPlaces;
    return [];
  }, [query, placeSearch, fuzzyBundledPlaces]);

  // Popular suggestions: editor's picks + first itinerary + first article + first neighborhood
  const popularSuggestions = useMemo<ResultGroup[]>(() => {
    const picks: ResultGroup[] = [];
    const editorsPicks = PLACES_CATALOG.filter(
      (p) => p.flags?.isEditorsPick
    ).slice(0, 3);
    editorsPicks.forEach((p) => {
      picks.push({
        type: 'place',
        id: p.id,
        name: p.name,
        subtitle: {
          en: `${p.categoryLabel.en} • ${p.neighborhood.en}`,
          ar: `${p.categoryLabel.ar} • ${p.neighborhood.ar}`
        },
        image: p.coverImage
      });
    });
    const firstItin = ITINERARIES[0] as any;
    if (firstItin) {
      picks.push({
        type: 'itinerary',
        id: firstItin.id,
        name: firstItin.title || firstItin.name,
        subtitle: firstItin.theme ||
        firstItin.duration || {
          en: 'Itinerary',
          ar: 'مسار'
        },
        image: firstItin.coverImage
      });
    }
    const firstArticle = EDITORIAL_POSTS[0] as any;
    if (firstArticle) {
      picks.push({
        type: 'editorial',
        id: firstArticle.slug,
        name: firstArticle.title,
        subtitle: firstArticle.excerpt || {
          en: 'Article',
          ar: 'مقال'
        },
        image: firstArticle.coverImage
      });
    }
    return picks;
  }, []);

  const grouped = useMemo(() => {
    const groups: Record<ResultType, ResultGroup[]> = {
      place: [],
      neighborhood: [],
      itinerary: [],
      editorial: [],
    };
    resolvedPlaceRows.forEach((r) => {
      if (groups.place.length < MAX_PER_GROUP) groups.place.push(r);
    });
    filteredNonPlaces.forEach((r) => {
      if (groups[r.type].length < MAX_PER_GROUP) groups[r.type].push(r);
    });
    return groups;
  }, [resolvedPlaceRows, filteredNonPlaces]);
  /** Flat list for keyboard nav, preserving group display order */
  const flatList = useMemo<ResultGroup[]>(() => {
    if (!query.trim()) return popularSuggestions;
    return [
      ...grouped.place,
      ...grouped.neighborhood,
      ...grouped.itinerary,
      ...grouped.editorial,
    ];
  }, [grouped, query, popularSuggestions]);
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);
  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatList.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const item = flatList[activeIndex];
        if (item) {
          e.preventDefault();
          navigate(ROUTES[item.type](item.id));
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, flatList, activeIndex, navigate]);
  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-result-index="${activeIndex}"]`
    );
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [activeIndex]);

  const placeResultCount =
    !query.trim()
      ? 0
      : placeSearch.kind === 'ok'
        ? placeSearch.rows.length
        : placeSearch.kind === 'fallback'
          ? fuzzyBundledPlaces.length
          : placeSearch.kind === 'loading'
            ? 0
            : 0;

  const totalCount = placeResultCount + filteredNonPlaces.length;

  const placesSearching =
    Boolean(query.trim()) && placeSearch.kind === 'loading';

  const GROUP_LABELS = {
    place: {
      en: 'Places',
      ar: 'الأماكن'
    },
    neighborhood: {
      en: 'Neighborhoods',
      ar: 'الأحياء'
    },
    itinerary: {
      en: 'Itineraries',
      ar: 'المسارات'
    },
    editorial: {
      en: 'Editorial',
      ar: 'المقالات'
    }
  };
  // Map flat index → which item is currently at that index
  const indexFor = (item: ResultGroup) =>
  flatList.findIndex((i) => i.type === item.type && i.id === item.id);
  return (
    <AnimatePresence>
      {isOpen &&
      <div className="fixed inset-0 z-[90]" dir={isRTL ? 'rtl' : 'ltr'}>
          <motion.div
          initial={{
            opacity: 0
          }}
          animate={{
            opacity: 1
          }}
          exit={{
            opacity: 0
          }}
          className="absolute inset-0 bg-sea-blue/40 backdrop-blur-sm"
          onClick={onClose} />
        

          <motion.div
          initial={{
            y: -30,
            opacity: 0
          }}
          animate={{
            y: 0,
            opacity: 1
          }}
          exit={{
            y: -30,
            opacity: 0
          }}
          transition={{
            duration: 0.2
          }}
          className={`relative w-[calc(100%-1.5rem)] sm:w-auto max-w-2xl mx-auto mt-6 sm:mt-20 bg-cream rounded-2xl sm:rounded-3xl shadow-float overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[80vh] ${lang === 'ar' ? 'font-arabic' : ''}`}>
          
            <div className="shrink-0 px-4 sm:px-5 py-3 sm:py-4 bg-white border-b border-sea-blue/10 flex items-center gap-3">
              <Search size={20} className="text-sea-blue/40 shrink-0" />
              <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={ui.searchPlaceholder}
              className="flex-grow bg-transparent text-sea-blue placeholder:text-sea-blue/40 text-base sm:text-lg focus:outline-none min-w-0"
              aria-label={lang === 'ar' ? 'بحث' : 'Search'} />
            
              <button
              onClick={onClose}
              className="p-1.5 text-sea-blue/40 hover:text-sea-blue hover:bg-sand rounded-full transition-colors shrink-0"
              aria-label={lang === 'ar' ? 'إغلاق البحث' : 'Close search'}>
              
                <X size={18} />
              </button>
            </div>

            <div ref={listRef} className="flex-grow overflow-y-auto p-4 sm:p-5">
              {!query.trim() ?
            <div>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <TrendingUp size={13} className="text-terracotta" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-sea-blue/80">
                      {lang === 'ar' ? 'مقترحات' : 'Popular'}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {popularSuggestions.map((r, i) => {
                  const Icon = ICONS[r.type];
                  const isActive = i === activeIndex;
                  return (
                    <Link
                      key={`${r.type}-${r.id}`}
                      to={ROUTES[r.type](r.id)}
                      onClick={onClose}
                      data-result-index={i}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={`flex items-center gap-3 p-2.5 sm:p-2 rounded-xl transition-colors group ${isActive ? 'bg-white shadow-sm' : 'hover:bg-white/60'}`}>
                      
                          {r.image ?
                      <img
                        src={r.image}
                        alt=""
                        className="w-11 h-11 rounded-lg object-cover shrink-0 border border-sea-blue/10" /> :


                      <div className="w-11 h-11 rounded-lg bg-sand flex items-center justify-center shrink-0">
                              <Icon size={16} className="text-sea-blue/50" />
                            </div>
                      }
                          <div className="min-w-0 flex-grow">
                            <div className="font-bold text-sea-blue truncate text-sm">
                              {t(r.name)}
                            </div>
                            <div className="text-xs text-sea-blue/50 truncate">
                              {t(r.subtitle)}
                            </div>
                          </div>
                          <ArrowRight
                        size={14}
                        className={`text-sea-blue/30 group-hover:text-terracotta transition-all shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                      
                        </Link>);

                })}
                  </div>
                  <p className="text-center text-[11px] text-sea-blue/40 mt-4">
                    {ui.startTyping}
                  </p>
                </div> :
            placesSearching && totalCount === 0 ?
            <div className="text-center py-12">
                  <p className={`text-sea-blue/85 font-medium ${lang === 'ar' ? 'font-arabic' : ''}`}>
                    {lang === 'ar' ? 'جاري البحث عن الأماكن…' : 'Searching places…'}
                  </p>
                </div> :
            totalCount === 0 ?
            <div className="text-center py-12">
                  <p className="text-sea-blue/85 font-medium mb-1">
                    {ui.noResultsFor} "{query}"
                  </p>
                  <p className="text-sea-blue/50 text-sm">
                    {ui.differentKeyword}
                  </p>
                </div> :

            <div className="space-y-6">
                  {(Object.keys(grouped) as ResultType[]).map((type) => {
                const items = grouped[type];
                if (items.length === 0) return null;
                const Icon = ICONS[type];
                return (
                  <div key={type}>
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <Icon size={13} className="text-terracotta" />
                          <span className="text-[11px] font-bold uppercase tracking-wider text-sea-blue/80">
                            {t(GROUP_LABELS[type])}
                          </span>
                          <span className="ms-auto text-[11px] text-sea-blue/40 font-medium ltr-numerals">
                            {items.length}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {items.map((r) => {
                        const flatIdx = indexFor(r);
                        const isActive = flatIdx === activeIndex;
                        return (
                          <Link
                            key={`${r.type}-${r.id}`}
                            to={ROUTES[r.type](r.id)}
                            onClick={onClose}
                            data-result-index={flatIdx}
                            onMouseEnter={() => setActiveIndex(flatIdx)}
                            className={`flex items-center gap-3 p-2.5 sm:p-2 rounded-xl transition-colors group ${isActive ? 'bg-white shadow-sm ring-1 ring-terracotta/30' : 'hover:bg-white/60'}`}>
                            
                                {r.image ?
                            <img
                              src={r.image}
                              alt=""
                              className="w-11 h-11 rounded-lg object-cover shrink-0 border border-sea-blue/10" /> :


                            <div className="w-11 h-11 rounded-lg bg-sand flex items-center justify-center shrink-0">
                                    <Icon
                                size={16}
                                className="text-sea-blue/50" />
                              
                                  </div>
                            }
                                <div className="min-w-0 flex-grow">
                                  <div className="font-bold text-sea-blue truncate text-sm">
                                    <HighlightedText
                                  text={t(r.name)}
                                  query={query} />
                                
                                  </div>
                                  <div className="text-xs text-sea-blue/50 truncate">
                                    {t(r.subtitle)}
                                  </div>
                                </div>
                                <ArrowRight
                              size={14}
                              className={`text-sea-blue/30 group-hover:text-terracotta transition-all shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                            
                              </Link>);

                      })}
                        </div>
                      </div>);

              })}
                </div>
            }
            </div>

            <div className="shrink-0 px-4 sm:px-5 py-2.5 sm:py-3 border-t border-sea-blue/10 bg-white text-[11px] text-sea-blue/50 flex justify-between items-center gap-3">
              <div className="hidden sm:flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-sand/70 text-sea-blue/85 font-mono">
                    ↑↓
                  </kbd>
                  {lang === 'ar' ? 'تنقل' : 'navigate'}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-sand/70 text-sea-blue/85 font-mono">
                    ↵
                  </kbd>
                  {lang === 'ar' ? 'افتح' : 'open'}
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-sand/70 text-sea-blue/85 font-mono">
                    Esc
                  </kbd>
                  {ui.toClose}
                </span>
              </div>
              <span className="sm:hidden text-sea-blue/40">{ui.search}</span>
              {totalCount > 0 &&
            <span>
                  <span className="ltr-numerals">{totalCount}</span>{' '}
                  {totalCount === 1 ? ui.result : ui.results}
                </span>
            }
            </div>
          </motion.div>
        </div>
      }
    </AnimatePresence>);

}